import React, { useEffect, useMemo, useState } from 'react';
import { ApprovalForm, useDebounce } from '@common-fe/common-fe';
import { toNumber } from 'lodash';

import { LockboxCheckType, LockboxStatus, LockboxType } from '@/modules/employer/components/Lockbox/lockbox.types';
import LockboxInfoContainer from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxInfoContainer';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { FeatureFlags, useFeatureFlag } from '@/modules/featureFlags';

import useLockboxProcessingOptions from './useLockboxProcessingOptions';

interface ProcessingValues {
  value?: string;
  subValue?: string;
  note?: string;
}

const LockboxProcessing = () => {
  const [values, setValues] = useState<ProcessingValues>({});
  const debouncedValues = useDebounce<ProcessingValues>(values);
  const { originalLockbox, editableLockbox, setEditableLockbox, isEditMode, isDividingMode } = useLockboxDetailsStore();
  const isReturnedChecksEnabled = useFeatureFlag(FeatureFlags.RETURNED_CHECKS_LOCKBOX);

  const remainder = useMemo(() => toNumber(originalLockbox?.amount || 0)  - (editableLockbox?.totalAllocated || 0),
    [originalLockbox?.amount, editableLockbox?.totalAllocated]);

  const isClaimRepaymentFullCheckApprove= useMemo(() => !editableLockbox?.claimRepayments?.length,
    [editableLockbox]);

  const isClaimRepaymentApproveDisabled = useMemo(() => !!editableLockbox?.claimRepayments?.length && remainder > 0,
    [editableLockbox, remainder]);

  const isClaimRepaymentPartiallyApproveDisabled = useMemo(() => !editableLockbox?.claimRepayments?.length || remainder <= 0,
    [editableLockbox, remainder]);

  const hasCreatedRepaymentAmount = useMemo(() => !!originalLockbox?.createdClaimRepayments?.length,
    [originalLockbox?.createdClaimRepayments]);

  const { options } = useLockboxProcessingOptions({
    lockboxType: editableLockbox?.lockboxType,
    isClaimRepaymentApproveDisabled,
    isClaimRepaymentFullCheckApprove,
    isClaimRepaymentPartiallyApproveDisabled,
    currentLockboxStatus: values?.value as LockboxStatus,
    hasCreatedRepaymentAmount,
    isReturnedChecksEnabled,
    selectedCheckType: editableLockbox?.selectedCheckType as LockboxCheckType | undefined,
  });

  useEffect(() => {
    if (editableLockbox?.lockboxType !== LockboxType.CLAIM_REPAYMENT) {
      return;
    }

    if (editableLockbox?.lockboxStatus === LockboxStatus.APPROVED && isClaimRepaymentApproveDisabled && !isClaimRepaymentFullCheckApprove) {
      setValues({ value: undefined });
    }
  
    if (editableLockbox?.lockboxStatus === LockboxStatus.PARTIALLY_APPROVED && isClaimRepaymentPartiallyApproveDisabled) {
      setValues({ value: undefined });
    }
  }, [editableLockbox, isClaimRepaymentApproveDisabled, isClaimRepaymentPartiallyApproveDisabled, isClaimRepaymentFullCheckApprove]);

  useEffect(() => {
    if (Object.keys(debouncedValues).length) {
      setEditableLockbox({
        lockboxStatus: debouncedValues?.value as LockboxStatus,
        reason: debouncedValues?.subValue,
        notes: debouncedValues?.note,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValues]);

  useEffect(() => {
    setValues({});
  }, [editableLockbox?.lockboxType, editableLockbox?.selectedCheckType]);

  useEffect(() => {
    setEditableLockbox({
      lockboxStatus: undefined,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableLockbox?.selectedCheckType]);


  if (!isEditMode || isDividingMode || !editableLockbox?.lockboxType || editableLockbox?.isMultipleRecordsWarning || editableLockbox?.lockboxType === LockboxType.UNKNOWN) {
    return null;
  }

  return (
    <LockboxInfoContainer data-testId="lockbox-processing" margin={{ top: 'spacing8' }}>
      <ApprovalForm
        values={values}
        onChangeValues={setValues}
        options={options}
      />
    </LockboxInfoContainer>
  );
};

export default LockboxProcessing;
